// Form wrapper around the Autocomplete UI primitive. Provides the
// same label / required / helper / error / aria-describedby shell
// every other form field uses (Field), so Tasks 5-7 can drop it
// alongside TextField / SelectField without UX drift.

import { useId } from "react";

import { Autocomplete } from "../ui/Autocomplete";
import { Field } from "./Field";

interface AutocompleteFieldProps<T> {
  label: string;
  items: ReadonlyArray<T>;
  value: T | null;
  onSelect: (item: T | null) => void;
  getKey: (item: T) => string;
  getLabel: (item: T) => string;
  getSecondary?: (item: T) => string | undefined;
  required?: boolean;
  helper?: React.ReactNode;
  error?: string | null;
  placeholder?: string;
  disabled?: boolean;
  allowClear?: boolean;
}

export function AutocompleteField<T>({
  label,
  items,
  value,
  onSelect,
  getKey,
  getLabel,
  getSecondary,
  required,
  helper,
  error,
  placeholder,
  disabled,
  allowClear,
}: AutocompleteFieldProps<T>) {
  const id = useId();
  return (
    <Field label={label} htmlFor={id} required={required} helper={helper} error={error}>
      {/* The underlying Autocomplete renders its own <input>; the
          Field <label> still associates with it via htmlFor + the
          input's wrapping label-click target. We pass id only so the
          required-asterisk aria semantics work. */}
      <div id={id} aria-describedby={error ? `${id}-error` : undefined}>
        <Autocomplete
          items={items}
          value={value}
          onSelect={onSelect}
          getKey={getKey}
          getLabel={getLabel}
          getSecondary={getSecondary}
          placeholder={placeholder}
          disabled={disabled}
          allowClear={allowClear}
        />
      </div>
    </Field>
  );
}
